import { endOfMonth, endOfWeek, isEqual, startOfMonth, startOfWeek } from 'date-fns';
import DateUtils from 'utils/dateUtils';
import { joinTextArray } from 'utils/commonUtils';
import { pluckValuesFromArrayWithSpecificKey } from 'utils/formatters';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import { JobForMapsFilter, JobListCardItem } from './dispatch.model';
import { AmountFilterOperation } from 'constants/common';
import { InvoiceStatus } from 'containers/invoices/invoices.model';
import { JobStatus } from 'containers/jobs/constant';
import { BrokenCircleTickIcon, InProgressIcon, ThumbsUpIcon } from 'assets/icons';
import {
  ArchivedPinIcon,
  CancelledPinIcon,
  ConfirmedPinIcon,
  DonePendingApprovalPinIcon,
  DonePinIcon,
  InProgressPinIcon,
  InboxPinIcon,
  PendingPinIcon,
  SubmittedPinIcon,
  SubmittedStatusIcon
} from 'assets/icons/job-statuses-icon-in-map';
import { MAP_SYSTEM_VIEW_SCHEDULE_PRESETS, SchedulePreset, ViewType } from './constants';
import { TeamView } from './dispatch.model';
import { getMarkerPinColor } from './utils/markerColors';
import { applyJobListSseUpdate } from './utils/jobListSse';

export { getMarkerPinColor, getStatusPinColor } from './utils/markerColors';

export const checkCustomFieldSelected = (
  range: { startDate: Date; endDate: Date },
  filterStartDate?: Date,
  filterEndDate?: Date
) => {
  const currentStart = filterStartDate?.toDateString();
  const currentEnd = filterEndDate?.toDateString();
  const rangeStart = range.startDate?.toDateString();
  const rangeEnd = range.endDate?.toDateString();

  return currentStart === rangeStart && currentEnd === rangeEnd;
};

export const getDateDefaultRangeForMaps = () => {
  const currentDate = new Date();
  const tomorrowDate = DateUtils.getTomorrow() as Date;
  const lastWeekStart = new Date(currentDate);
  lastWeekStart.setDate(currentDate.getDate() - 7);
  const lastMonthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
  const oneWeekLaterDate = new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
  const twoWeekLaterDate = new Date(currentDate.getTime() + 14 * 24 * 60 * 60 * 1000);

  const scheduledForDateStaticRanges = [
    {
      label: 'Today',
      isSelected: range => checkCustomFieldSelected(range, currentDate, currentDate),
      range: () => ({
        startDate: currentDate,
        endDate: currentDate
      })
    },
    {
      label: 'Tomorrow',
      isSelected: range => checkCustomFieldSelected(range, tomorrowDate, tomorrowDate),
      range: () => ({
        startDate: tomorrowDate,
        endDate: tomorrowDate
      })
    },
    {
      label: 'Next 7 days',
      isSelected: range => checkCustomFieldSelected(range, currentDate, oneWeekLaterDate),
      range: () => ({
        startDate: currentDate,
        endDate: oneWeekLaterDate
      })
    },
    {
      label: 'Next 14 days',
      isSelected: range => checkCustomFieldSelected(range, currentDate, twoWeekLaterDate),
      range: () => ({
        startDate: currentDate,
        endDate: twoWeekLaterDate
      })
    },
    {
      label: 'This week',
      isSelected: range => checkCustomFieldSelected(range, startOfWeek(currentDate), endOfWeek(currentDate)),
      range: () => ({
        startDate: startOfWeek(currentDate),
        endDate: endOfWeek(currentDate)
      })
    },
    {
      label: 'Last week',
      isSelected: range => checkCustomFieldSelected(range, startOfWeek(lastWeekStart), endOfWeek(lastWeekStart)),
      range: () => {
        return {
          startDate: startOfWeek(lastWeekStart),
          endDate: endOfWeek(lastWeekStart)
        };
      }
    },
    {
      label: 'This month',
      isSelected: range => checkCustomFieldSelected(range, startOfMonth(currentDate), endOfMonth(currentDate)),
      range: () => ({
        startDate: startOfMonth(currentDate),
        endDate: endOfMonth(currentDate)
      })
    },
    {
      label: 'Last month',
      isSelected: range => checkCustomFieldSelected(range, startOfMonth(lastMonthDate), endOfMonth(lastMonthDate)),
      range: () => {
        return {
          startDate: startOfMonth(lastMonthDate),
          endDate: endOfMonth(lastMonthDate)
        };
      }
    },
    {
      label: 'This year',
      isSelected: range =>
        checkCustomFieldSelected(
          range,
          DateUtils.getStartOfYear(currentDate.getFullYear()),
          DateUtils.getEndOfYear(currentDate.getFullYear())
        ),
      range: () => ({
        startDate: DateUtils.getStartOfYear(currentDate.getFullYear()),
        endDate: DateUtils.getEndOfYear(currentDate.getFullYear())
      })
    },
    {
      label: 'Last year',
      isSelected: range =>
        checkCustomFieldSelected(
          range,
          DateUtils.getStartOfYear(currentDate.getFullYear() - 1),
          DateUtils.getEndOfYear(currentDate.getFullYear() - 1)
        ),
      range: () => ({
        startDate: DateUtils.getStartOfYear(currentDate.getFullYear() - 1),
        endDate: DateUtils.getEndOfYear(currentDate.getFullYear() - 1)
      })
    },
    {
      label: 'Any time',
      isSelected: range => checkCustomFieldSelected(range, new Date(0), null),
      range: () => ({
        startDate: new Date(0),
        endDate: null
      })
    },
    {
      label: 'Unscheduled',
      isSelected: range => checkCustomFieldSelected(range, null, null),
      range: () => ({
        startDate: null,
        endDate: null
      })
    }
  ];
  return scheduledForDateStaticRanges;
};

export const calculateDateRangeLabel = (startDate: Date | string | null, endDate: Date | string | null): string => {
  if (startDate && !endDate) {
    return 'Any time';
  }

  if (!startDate || !endDate) return 'Unscheduled';

  // Convert string dates to Date objects if needed
  const startOfStartDate = DateUtils.getStartOfDay(startDate);
  const startOfEndDate = DateUtils.getStartOfDay(endDate);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if it's Today
  const todayStart = DateUtils.getStartOfDay(today);

  if (isEqual(startOfStartDate, todayStart) && isEqual(startOfEndDate, todayStart)) {
    return 'Today';
  }

  // Check if it's Tomorrow
  const tomorrow = DateUtils.getTomorrow() as Date;
  const tomorrowStart = DateUtils.getStartOfDay(tomorrow);

  if (isEqual(startOfStartDate, tomorrowStart) && isEqual(startOfEndDate, tomorrowStart)) {
    return 'Tomorrow';
  }

  // Check if it's Next 7 days
  const oneWeekLater = DateUtils.addDaysToDate(today, 7);
  const oneWeekLaterStart = DateUtils.getStartOfDay(oneWeekLater);

  if (isEqual(startOfStartDate, todayStart) && isEqual(startOfEndDate, oneWeekLaterStart)) {
    return 'Next 7 days';
  }

  // Check if it's Next 14 days
  const twoWeeksLater = DateUtils.addDaysToDate(today, 14);
  const twoWeeksLaterStart = DateUtils.getStartOfDay(twoWeeksLater);

  if (isEqual(startOfStartDate, todayStart) && isEqual(startOfEndDate, twoWeeksLaterStart)) {
    return 'Next 14 days';
  }

  // Get all static ranges including custom ones
  const allRanges = getDateDefaultRangeForMaps();

  // Check for all predefined ranges
  for (const range of allRanges) {
    const rangeResult = range.range();
    if (rangeResult.startDate && rangeResult.endDate) {
      const rangeStartOfDay = DateUtils.getStartOfDay(rangeResult.startDate);
      const rangeStartOfEndDate = DateUtils.getStartOfDay(rangeResult.endDate);

      if (isEqual(startOfStartDate, rangeStartOfDay) && isEqual(startOfEndDate, rangeStartOfEndDate)) {
        return range.label;
      }
    }
  }

  // If no predefined range matches, return a custom format
  return 'Custom';
};

export const getTextForFilter = (
  filterData: any[],
  selectedArray: string[],
  dataIdKey: string,
  dataKey: string,
  secondaryDataKey?: string
) => {
  if (selectedArray) {
    return (
      joinTextArray(
        pluckValuesFromArrayWithSpecificKey(
          filterData.filter(item => selectedArray.includes(item[dataIdKey])),
          dataKey,
          secondaryDataKey
        )
      ) || ''
    );
  }
  return '';
};

export const getViewFilters = (viewFilters: JobForMapsFilter | undefined) => {
  if (!viewFilters) {
    return {};
  }

  const {
    customerIds,
    assignees,
    createdBy,
    status,
    leadSource,
    scheduledFrom,
    scheduledTo,
    invoiceStatus,
    estimateStatus,
    amount,
    zipCode,
    radius,
    isToday,
    schedulePreset,
    externalCompany,
    tags,
    isUnscheduled,
    isUnassigned,
    serviceArea,
    createdFrom,
    createdTo,
    noTags
  } = viewFilters;

  const filters: Record<string, string[] | string | number | AmountFilter | boolean | null> = {};

  if (customerIds) filters.customerIds = customerIds.split(',');
  if (assignees) filters.assignees = assignees.split(',');
  if (createdBy) filters.createdBy = createdBy.split(',');
  if (status) filters.status = status.split(',');
  if (leadSource) filters.leadSource = leadSource.split(',');
  if (scheduledFrom) filters.scheduledFrom = scheduledFrom;
  if (scheduledTo) filters.scheduledTo = scheduledTo;
  if (invoiceStatus) filters.invoiceStatus = invoiceStatus.split(',');
  if (estimateStatus) filters.estimateStatus = estimateStatus.split(',');
  if (amount) filters.amount = reverseAmountQueryParam(amount);
  if (zipCode) filters.zipCode = zipCode;
  filters.radius = radius;

  // Handle special date filtering cases
  if (isToday) {
    filters.scheduledFrom = DateUtils.getUTCStartDate(new Date());
    filters.scheduledTo = DateUtils.getUTCEndDate(new Date());
  } else if (schedulePreset) {
    const dateRange = getDateRangeFromSchedulePreset(schedulePreset);
    if (dateRange.startDate && dateRange.endDate) {
      filters.scheduledFrom = DateUtils.getUTCStartDate(dateRange.startDate);
      filters.scheduledTo = DateUtils.getUTCEndDate(dateRange.endDate);
    }
  }

  filters.schedulePreset = schedulePreset;
  filters.isToday = isToday;
  if (externalCompany) filters.externalCompany = externalCompany.split(',');
  if (tags) filters.tags = (tags as any).split(',').map(Number);
  if (noTags) filters.noTags = noTags;
  if (isUnscheduled) filters.isUnscheduled = isUnscheduled;
  if (isUnassigned) filters.isUnassigned = isUnassigned;
  if (serviceArea) filters.serviceArea = serviceArea.split(',');
  if (createdFrom) filters.createdFrom = createdFrom;
  if (createdTo) filters.createdTo = createdTo;
  return filters;
};

export const getViewFiltersFromTeamView = (view: TeamView | null | undefined) => {
  const filters = getViewFilters(view?.filters);
  const systemSchedulePreset = view?.name ? MAP_SYSTEM_VIEW_SCHEDULE_PRESETS[view.name] : undefined;

  if (!systemSchedulePreset) return filters;

  const dateRange = getDateRangeFromSchedulePreset(systemSchedulePreset);
  if (!dateRange.startDate || !dateRange.endDate) return filters;

  filters.scheduledFrom = DateUtils.getUTCStartDate(dateRange.startDate);
  filters.scheduledTo = DateUtils.getUTCEndDate(dateRange.endDate);
  filters.schedulePreset = systemSchedulePreset;
  filters.isToday = false;
  filters.isUnscheduled = false;

  return filters;
};

export const getAmountQueryParam = (amountFilter: AmountFilter) => {
  const { filterOperation, minAmount, maxAmount } = amountFilter;
  if (filterOperation === AmountFilterOperation.ALL) return null;
  if (filterOperation === AmountFilterOperation.BW) return `${filterOperation}:${minAmount}:${maxAmount}`;
  return `${filterOperation}:${minAmount}`;
};

export const reverseAmountQueryParam = (queryParam: string | null): AmountFilter | null => {
  if (!queryParam) return { filterOperation: AmountFilterOperation.ALL, minAmount: null, maxAmount: null };

  const [operation, min, max] = queryParam.split(':');
  const filterOperation = operation as AmountFilterOperation;

  if (filterOperation === AmountFilterOperation.BW) {
    return {
      filterOperation,
      minAmount: parseFloat(min),
      maxAmount: parseFloat(max)
    };
  }

  return {
    filterOperation,
    minAmount: parseFloat(min),
    maxAmount: null
  };
};

export const isInvoicePaid = (invoiceStatus: string) => {
  return invoiceStatus === InvoiceStatus.PAID || invoiceStatus === InvoiceStatus.PAID_QR;
};

export const prepareStartDateForSave = (date: string) => {
  return date && typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? DateUtils.startDateToUTCString(date)
    : date;
};

export const prepareEndDateForSave = (date: string) => {
  return date && typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? DateUtils.endDateToUTCString(date)
    : date;
};

export const prepareDatesForComparison = (date: string) => {
  return date && typeof date === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(date)
    ? DateUtils.getDateInFormat({ date: new Date(date), dateFormat: 'yyyy-MM-dd' })
    : date;
};

export const getFirstActionButton = (
  status: JobStatus,
  setJobStatus: (status: JobStatus) => void,
  setEditStatusModal: (editStatusModal: boolean) => void,
  canMarkJobDone: boolean
) => {
  switch (status) {
    case JobStatus.INBOX:
      return (
        <button
          className="flex w-[68px] cursor-pointer flex-col items-center gap-[5px]"
          id="submit-job-button"
          onClick={() => {
            setEditStatusModal(true);
            setJobStatus(JobStatus.SUBMITTED);
          }}>
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full bg-primaryBtn
    text-primaryBtnText hover:bg-primaryButtonHover`}>
            <SubmittedStatusIcon className="path path-fill-current h-[22px] w-[22px] text-white" />
          </div>
          <div className="w-full text-center text-xs font-medium text-secondary">
            Submit
            <br /> job
          </div>
        </button>
      );
    case JobStatus.SUBMITTED:
      return (
        <button
          className="flex w-[68px] cursor-pointer flex-col items-center gap-[5px]"
          id="confirm-job-button"
          onClick={() => {
            setEditStatusModal(true);
            setJobStatus(JobStatus.CONFIRMED);
          }}>
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full bg-primaryBtn
    text-primaryBtnText hover:bg-primaryButtonHover`}>
            <ThumbsUpIcon className="h-[22px] w-[22px]" />
          </div>
          <div className="w-full text-center text-xs font-medium text-secondary">
            Confirm
            <br /> job
          </div>
        </button>
      );
    case JobStatus.CONFIRMED:
      return (
        <button
          className="flex w-[68px] cursor-pointer flex-col items-center gap-[5px]"
          id="set-in-progress-button"
          onClick={() => {
            setEditStatusModal(true);
            setJobStatus(JobStatus.IN_PROGRESS);
          }}>
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full bg-primaryBtn
    text-primaryBtnText hover:bg-primaryButtonHover`}>
            <InProgressIcon className="h-[22px] w-[22px]" />
          </div>
          <div className="w-full text-center text-xs font-medium text-secondary">
            Set as <br /> In progress
          </div>
        </button>
      );
    case JobStatus.IN_PROGRESS:
    case JobStatus.DONE_PENDING_APPROVAL:
      if (status !== JobStatus.DONE_PENDING_APPROVAL || canMarkJobDone) {
        return (
          <button
            className="flex w-[68px] cursor-pointer flex-col items-center gap-[5px]"
            id="mark-done-button"
            onClick={() => {
              setEditStatusModal(true);
              if (canMarkJobDone) {
                setJobStatus(JobStatus.DONE);
              } else {
                setJobStatus(JobStatus.DONE_PENDING_APPROVAL);
              }
            }}>
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-full bg-primaryBtn
    text-primaryBtnText hover:bg-primaryButtonHover`}>
              <BrokenCircleTickIcon className="h-[22px] w-[22px]" />
            </div>
            <div className="w-full text-center text-xs font-medium text-secondary">
              {canMarkJobDone ? (
                <>
                  Mark as <br /> done
                </>
              ) : (
                <>
                  Mark as <br /> done (pending approval)
                </>
              )}
            </div>
          </button>
        );
      }
      break;
    case JobStatus.PENDING:
      return (
        <button
          className="flex w-[68px] cursor-pointer flex-col items-center gap-[5px]"
          id="set-in-progress-button"
          onClick={() => {
            setEditStatusModal(true);
            setJobStatus(JobStatus.IN_PROGRESS);
          }}>
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-full bg-primaryBtn
          text-primaryBtnText hover:bg-primaryButtonHover`}>
            <InProgressIcon className="h-[22px] w-[22px]" />
          </div>
          <div className="w-full text-center text-xs font-medium text-secondary">
            Set as <br /> In progress
          </div>
        </button>
      );
  }
};

export const getPinIcon = (status: JobStatus, isSelected: boolean, pinColor?: string) => {
  const scaleClassName = isSelected ? 'scale-125' : '';
  const pinStyle = pinColor ? { color: pinColor } : {};

  switch (status) {
    case JobStatus.INBOX:
      return <InboxPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#3E556D' }} />;
    case JobStatus.SUBMITTED:
      return <SubmittedPinIcon className={`${scaleClassName} `} style={pinColor ? pinStyle : { color: '#00ABB8' }} />;
    case JobStatus.CONFIRMED:
      return <ConfirmedPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#4A8DF2' }} />;
    case JobStatus.IN_PROGRESS:
      return <InProgressPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#F88A29' }} />;
    case JobStatus.DONE:
      return <DonePinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#27AE60' }} />;
    case JobStatus.PENDING:
      return <PendingPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#DBAC00' }} />;
    case JobStatus.DONE_PENDING_APPROVAL:
      return (
        <DonePendingApprovalPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#BD68DC' }} />
      );
    case JobStatus.CANCELED:
      return <CancelledPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#D95945' }} />;
    case JobStatus.ARCHIVED:
      return <ArchivedPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#828282' }} />;
    case JobStatus.REJECTED:
      return <CancelledPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#D95945' }} />;
    default:
      return <SubmittedPinIcon className={scaleClassName} style={pinColor ? pinStyle : { color: '#00ABB8' }} />;
  }
};

export const getDateRangeFromSchedulePreset = (preset: SchedulePreset | string) => {
  const today = new Date();
  switch (preset) {
    case SchedulePreset.TODAY:
      return {
        startDate: today,
        endDate: today
      };
    case SchedulePreset.TOMORROW: {
      const tomorrow = DateUtils.getTomorrow() as Date;
      return {
        startDate: tomorrow,
        endDate: tomorrow
      };
    }
    case SchedulePreset.NEXT_7_DAYS:
      return {
        startDate: today,
        endDate: new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
      };
    case SchedulePreset.NEXT_14_DAYS:
      return {
        startDate: today,
        endDate: new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000)
      };
    case SchedulePreset.THIS_WEEK:
      return {
        startDate: startOfWeek(today),
        endDate: endOfWeek(today)
      };
    case SchedulePreset.LAST_WEEK: {
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - 7);
      return {
        startDate: startOfWeek(lastWeekStart),
        endDate: endOfWeek(lastWeekStart)
      };
    }
    case SchedulePreset.THIS_MONTH:
      return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today)
      };
    case SchedulePreset.LAST_MONTH: {
      const lastMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return {
        startDate: startOfMonth(lastMonthDate),
        endDate: endOfMonth(lastMonthDate)
      };
    }
    case SchedulePreset.THIS_YEAR:
      return {
        startDate: DateUtils.getStartOfYear(today.getFullYear()),
        endDate: DateUtils.getEndOfYear(today.getFullYear())
      };
    case SchedulePreset.LAST_YEAR:
      return {
        startDate: DateUtils.getStartOfYear(today.getFullYear() - 1),
        endDate: DateUtils.getEndOfYear(today.getFullYear() - 1)
      };
    default:
      return { startDate: null, endDate: null };
  }
};

export const getSchedulePresetFromLabel = (label: string): SchedulePreset | null => {
  switch (label) {
    case 'Today':
      return SchedulePreset.TODAY;
    case 'Tomorrow':
      return SchedulePreset.TOMORROW;
    case 'Next 7 days':
      return SchedulePreset.NEXT_7_DAYS;
    case 'Next 14 days':
      return SchedulePreset.NEXT_14_DAYS;
    case 'This week':
      return SchedulePreset.THIS_WEEK;
    case 'Last week':
      return SchedulePreset.LAST_WEEK;
    case 'This month':
      return SchedulePreset.THIS_MONTH;
    case 'Last month':
      return SchedulePreset.LAST_MONTH;
    case 'This year':
      return SchedulePreset.THIS_YEAR;
    case 'Last year':
      return SchedulePreset.LAST_YEAR;
    default:
      return null;
  }
};

/**
 * Format map view filters for API calls
 * This utility function standardizes filter formatting for map components
 */
export const formatMapViewFiltersForAPI = (currentViewFilters: any) => {
  const isSpecialDateCase = currentViewFilters.isToday || currentViewFilters.schedulePreset;

  let scheduledFromValue: string | null = '';
  let scheduledToValue: string | null = '';

  if (isSpecialDateCase) {
    scheduledFromValue = null;
    scheduledToValue = null;
  } else {
    scheduledFromValue = currentViewFilters.scheduledFrom
      ? prepareStartDateForSave(currentViewFilters.scheduledFrom as string)
      : '';
    scheduledToValue = currentViewFilters.scheduledTo
      ? prepareEndDateForSave(currentViewFilters.scheduledTo as string)
      : '';
  }

  // Handle assignees filter to exclude 'isUnassigned' for team filter
  const teamFilter = currentViewFilters.assignees
    ? (currentViewFilters.assignees as string[]).filter(a => a !== 'isUnassigned')
    : '';

  const formattedFilters = {
    customerIds: currentViewFilters.customerIds ? (currentViewFilters.customerIds as string[]).join(',') : '',
    assignees: teamFilter ? teamFilter.join(',') : '',
    createdBy: currentViewFilters.createdBy ? (currentViewFilters.createdBy as string[]).join(',') : '',
    status: currentViewFilters.status ? (currentViewFilters.status as string[]).join(',') : '',
    leadSource: currentViewFilters.leadSource ? (currentViewFilters.leadSource as string[]).join(',') : '',
    scheduledFrom: scheduledFromValue,
    scheduledTo: scheduledToValue,
    invoiceStatus: currentViewFilters.invoiceStatus ? (currentViewFilters.invoiceStatus as string[]).join(',') : '',
    estimateStatus: currentViewFilters.estimateStatus ? (currentViewFilters.estimateStatus as string[]).join(',') : '',
    amount: currentViewFilters.amount ? getAmountQueryParam(currentViewFilters.amount as AmountFilter) : null,
    zipCode: currentViewFilters.zipCode || '',
    radius: currentViewFilters.radius || 0,
    externalCompany: currentViewFilters.externalCompany
      ? (currentViewFilters.externalCompany as string[]).join(',')
      : '',
    isToday: currentViewFilters.isToday as boolean,
    schedulePreset: currentViewFilters.schedulePreset as string,
    tags: currentViewFilters.tags ? (currentViewFilters.tags as string[]).join(',') : '',
    noTags: currentViewFilters.noTags || null,
    isUnscheduled: currentViewFilters.isUnscheduled as boolean,
    isUnassigned: currentViewFilters.isUnassigned as boolean,
    serviceArea: currentViewFilters.serviceArea ? (currentViewFilters.serviceArea as string[]).join(',') : '',
    createdFrom: currentViewFilters.createdFrom
      ? prepareStartDateForSave(currentViewFilters.createdFrom as string)
      : '',
    createdTo: currentViewFilters.createdTo ? prepareEndDateForSave(currentViewFilters.createdTo as string) : ''
  };

  // Filter out empty values to keep the payload clean
  return Object.entries(formattedFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
};

export const convertToSeconds = (milliseconds: number): number => {
  return Number((milliseconds / 1000).toFixed(3));
};

export const formatFilterParamsForAPI = (currentViewFilters: any) => {
  let scheduledFromValue = null;
  let scheduledToValue = currentViewFilters.scheduledTo as string;

  if (currentViewFilters.scheduledFrom) {
    scheduledFromValue = currentViewFilters.scheduledFrom as string;
  } else if (currentViewFilters.isUnscheduled) {
    scheduledToValue = null;
  } else if (currentViewFilters.schedulePreset) {
    const dateRange = getDateRangeFromSchedulePreset(currentViewFilters.schedulePreset as string);
    if (dateRange.startDate && dateRange.endDate) {
      scheduledFromValue = DateUtils.getDateInFormat({ date: dateRange.startDate, dateFormat: 'yyyy-MM-dd' });
      scheduledToValue = DateUtils.getDateInFormat({ date: dateRange.endDate, dateFormat: 'yyyy-MM-dd' });
    }
  } else {
    if (Array.isArray(currentViewFilters.status)) {
      const isUnscheduled = currentViewFilters.scheduledTo === null;
      if (isUnscheduled) {
        scheduledFromValue = null;
      }
    }
  }

  scheduledFromValue = scheduledFromValue ? prepareStartDateForSave(scheduledFromValue) : '';
  scheduledToValue = scheduledToValue ? prepareEndDateForSave(scheduledToValue) : '';

  // Handle assignees filter to exclude 'isUnassigned' for team filter
  const teamFilter = currentViewFilters.assignees
    ? (currentViewFilters.assignees as string[]).filter(a => a !== 'isUnassigned')
    : '';

  const formattedFilters = {
    customerIds: currentViewFilters.customerIds ? (currentViewFilters.customerIds as string[]).join(',') : '',
    assignees: teamFilter ? teamFilter.join(',') : '',
    createdBy: currentViewFilters.createdBy ? (currentViewFilters.createdBy as string[]).join(',') : '',
    status: currentViewFilters.status ? (currentViewFilters.status as string[]).join(',') : '',
    leadSource: currentViewFilters.leadSource ? (currentViewFilters.leadSource as string[]).join(',') : '',
    scheduledFrom: scheduledFromValue,
    scheduledTo: scheduledToValue,
    invoiceStatus: currentViewFilters.invoiceStatus ? (currentViewFilters.invoiceStatus as string[]).join(',') : '',
    estimateStatus: currentViewFilters.estimateStatus ? (currentViewFilters.estimateStatus as string[]).join(',') : '',
    amount: currentViewFilters.amount ? getAmountQueryParam(currentViewFilters.amount as AmountFilter) : null,
    zipCode: currentViewFilters.zipCode || '',
    radius: currentViewFilters.radius || 0,
    externalCompany: currentViewFilters.externalCompany
      ? (currentViewFilters.externalCompany as string[]).join(',')
      : '',
    tags: currentViewFilters.tags ? (currentViewFilters.tags as string[]).join(',') : '',
    noTags: currentViewFilters.noTags || null,
    isUnscheduled: currentViewFilters.isUnscheduled as boolean,
    isUnassigned: currentViewFilters.isUnassigned as boolean,
    serviceArea: currentViewFilters.serviceArea ? (currentViewFilters.serviceArea as string[]).join(',') : '',
    createdFrom: currentViewFilters.createdFrom
      ? prepareStartDateForSave(currentViewFilters.createdFrom as string)
      : '',
    createdTo: currentViewFilters.createdTo ? prepareEndDateForSave(currentViewFilters.createdTo as string) : ''
  };

  // Filter out empty values to keep the payload clean
  return Object.entries(formattedFilters).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
};

export const getAssigneesSignature = (job: JobListCardItem) =>
  (job.assignees ?? [])
    .map(assignee => assignee.id)
    .sort()
    .join(',');

export const getMarkerAppearanceKey = (job: JobListCardItem, mapView: ViewType) => {
  const pinColor = getMarkerPinColor(job, mapView);
  const assigneesKey = mapView === ViewType.MEMBER ? getAssigneesSignature(job) : '';

  return `${job.status}:${mapView}:${pinColor}:${assigneesKey}`;
};

export const modifyJobsListWithSSEData = ({
  jobsList,
  sseData
}: {
  jobsList: JobListCardItem[];
  sseData: Parameters<typeof applyJobListSseUpdate>[1];
}) => applyJobListSseUpdate(jobsList, sseData);
